import type { VerticalLine } from '../types';

/**
 * Sort vertical lines by timestamp (ascending)
 */
export function sortLinesByTimestamp(lines: VerticalLine[]): VerticalLine[] {
  return [...lines].sort((a: VerticalLine, b: VerticalLine) => {
    const aTime = typeof a.timestamp === 'number' ? a.timestamp : a.timestamp.getTime();
    const bTime = typeof b.timestamp === 'number' ? b.timestamp : b.timestamp.getTime();
    return aTime - bTime;
  });
}

/**
 * Get timestamp value as number (handles both Date and number types)
 */
export function getTimestampValue(timestamp: Date | number): number {
  return typeof timestamp === 'number' ? timestamp : timestamp.getTime();
}

/**
 * Find group lines by group ID or fallback to color/chartId matching
 */
export function findGroupLines(verticalLines: VerticalLine[], clickedLine: VerticalLine): VerticalLine[] {
  return verticalLines.filter((line: VerticalLine) => {
    // If we have groupId, use it for precise matching
    if (clickedLine.groupId && line.groupId) {
      return line.groupId === clickedLine.groupId;
    }
    // Fallback to old logic for backward compatibility (lines created before groupId was added)
    return line.isHarmonic && line.color === clickedLine.color && line.chartId === clickedLine.chartId;
  });
}

/**
 * Find the starting line (first harmonic) in a group
 */
export function findStartingLine(groupLines: VerticalLine[]): VerticalLine | undefined {
  return groupLines.find((line: VerticalLine) => line.isFirstHarmonic) || groupLines[0];
}

/**
 * Group markers by section
 */
export function groupMarkersBySection(verticalLines: VerticalLine[]): Record<string, VerticalLine[]> {
  const markersBySection: Record<string, VerticalLine[]> = {};

  verticalLines.forEach((line: VerticalLine) => {
    // Only process non-harmonic markers
    if (!line.isHarmonic && line.sectionKey) {
      if (!markersBySection[line.sectionKey]) {
        markersBySection[line.sectionKey] = [];
      }
      markersBySection[line.sectionKey].push(line);
    }
  });

  return markersBySection;
}

/**
 * Find the first marker in a section (sorted by timestamp)
 */
export function findFirstMarkerInSection(markers: VerticalLine[]): VerticalLine | undefined {
  if (markers.length === 0) return undefined;
  return sortLinesByTimestamp(markers)[0];
}

/**
 * Extract numeric value from a label (e.g., "H1" -> 1, "-C2" -> 2)
 */
export function extractLabelNumber(label: string): number {
  const match = label.match(/^-?[A-Z](\d+)$/);
  return match ? parseInt(match[1]) : 0;
}
